import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../services/authService';

const AuthTokenCallback = () => {
  const navigate = useNavigate();
  const handledRef = useRef(false); // Prevent double-run in React.StrictMode (dev)

  useEffect(() => {
    if (handledRef.current) return; // idempotent guard
    handledRef.current = true;

    const verifyUserAccess = async (token) => {
      try {
        // Verify if the user exists in the system
        const response = await fetch(`${API_BASE}/employees/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include'
        });

        if (response.status === 404) {
          // User not found in the system
          localStorage.removeItem('userToken');
          localStorage.removeItem('user');
          navigate('/unauthorized', { replace: true });
          return false;
        }

        if (!response.ok) {
          // Other errors
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const userData = await response.json();
        // Store user data
        localStorage.setItem('employee', JSON.stringify(userData));
        return true;
      } catch (error) {
        console.error('Failed to verify user access:', error);
        // On error, redirect to unauthorized
        localStorage.removeItem('userToken');
        localStorage.removeItem('user');
        navigate('/unauthorized', { replace: true });
        return false;
      }
    };

    const handleCallback = async () => {
      // Try to parse token from either URL hash (#token=...) or query (?token=...)
      const { hash, search, pathname } = window.location;
      const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
      const searchParams = new URLSearchParams(search);
      const token = hashParams.get('token') || searchParams.get('token');

      // If token is present, verify and persist
      if (token) {
        try {
          localStorage.setItem('userToken', token);
        } catch (_) {
          // ignore storage errors
        }
        
        // Verify user exists in system
        const isAuthorized = await verifyUserAccess(token);
        
        if (!isAuthorized) {
          return; // Already redirected to unauthorized
        }

        // Clean the URL (strip hash) without adding a history entry
        const cleanUrl = pathname + (search || '');
        try {
          window.history.replaceState(null, '', cleanUrl);
        } catch (_) {
          // fallback: no-op
        }
        // Redirect to dashboard, replace to avoid back-navigation to callback
        navigate('/home', { replace: true });
        return;
      }

      // If token missing but already logged in (token in storage), verify it
      const stored = (() => {
        try { return localStorage.getItem('userToken'); } catch { return null; }
      })();
      
      if (stored) {
        const isAuthorized = await verifyUserAccess(stored);
        if (isAuthorized) {
          navigate('/home', { replace: true });
        }
        return;
      }

      // No token found, go back to login
      navigate('/', { replace: true });
    };

    handleCallback();
  }, [navigate]);

  return null;
};

export default AuthTokenCallback;
